import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import lang from '@/lang'

import { UserService } from '@/modules/user/userService'
import SelectInput from '@/modules/base/components/inputs/select-input/select-input'
import TenantCollaboratorsService from '@/modules/tenant/tenantCollaboratorsService'

export const buildUserSelectOption = (user) => {
  const { id, firstName, lastName, email } = user
  const userName = `${firstName} ${lastName}`
  const emailAddress = userName.length ? ` - (${email})` : email

  return {
    value: id,
    label: userName + emailAddress,
    user,
  }
}

export function UserSelect(props) {
  const {
    onChange,
    customSelectOptions,
    tenantCollaborationId,
    hasClientModuleAccess,
    setResponsibleUserCount,
    hideTaxConsultantUsers,
    addAllOption = false,
    exclude = [],
    fileId,
    // Filtered by the endpoint, not here: it paginates, so dropping rows would leave short pages.
    excludeLocked = false,
    ...restProps
  } = props
  const [value, setValue] = useState(props.value)
  const isInternal = tenantCollaborationId === 'internal'

  useEffect(() => {
    setValue(props.value)
  }, [props.value])

  if (!isInternal && !tenantCollaborationId) return null

  const buildSelectOptions = (users) => {
    const userOptions = users.map(user => buildUserSelectOption(user))
    if (addAllOption && userOptions.length > 0) {
      return [
        {
          value: 'all',
          label: lang.selectInput.user.allUsers,
          user: userOptions.map(user => ({
            id: user.value,
            name: user.label,
            type: 'editor',
          })),
        },
        ...userOptions,
      ]
    } else {
      return userOptions
    }
  }

  const fetchUsers = async(searchTerm, loadedOptions) => {
    try {
      const offset = loadedOptions?.length || 0
      const { data: users } = await UserService.find(searchTerm, offset, 10, true, fileId, excludeLocked)
      const filteredUsers = users.filter(user => !exclude.includes(user.id))
      const selectOptions = buildSelectOptions(filteredUsers)
      const filteredCount = selectOptions.length

      const hasMore = (offset + selectOptions.length) !== filteredCount
      setResponsibleUserCount && setResponsibleUserCount(filteredCount)
      return {
        options: selectOptions,
        hasMore,
      }
    } catch (error) {
      console.error(error)
    }
  }

  const fetchCollaboratorUsers = async(searchTerm, loadedOptions, tenantCollaborationId, taxOfficeUsersFirst) => {
    const taxOfficeUsers = await fetchUsers(searchTerm, loadedOptions)
    const { data: users } = await TenantCollaboratorsService.getCollaboratorUsers(tenantCollaborationId, true)

    const mergedUsers = taxOfficeUsersFirst
      ? [...taxOfficeUsers.options, ...buildSelectOptions(users)]
      : [...buildSelectOptions(users), ...taxOfficeUsers.options]
    const usersToDisplay = hideTaxConsultantUsers ? [...await buildSelectOptions(users)] : mergedUsers
    setResponsibleUserCount && setResponsibleUserCount(mergedUsers.length)
    return {
      options: usersToDisplay,
      hasMore: false,
    }
  }

  const handleUserChange = (option) => {
    onChange && onChange(option)
    setValue(option)
  }

  const options = {
    infiniteScroll: true,
    allowCreate: false,
    customSelectOptions: {
      isClearable: false,
      hideSelectedOptions: false,
      placeholder: lang.selectInput.user.placeholder(false),
      defaultMenuIsOpen: false,
      defaultOptions: true,
      loadOptions: !isInternal ? (searchTerm, loadedOptions) => fetchCollaboratorUsers(searchTerm, loadedOptions, tenantCollaborationId, !hasClientModuleAccess) : fetchUsers,
      loadingMessage: lang.selectInput.user.loadingMessage,
      ...customSelectOptions,
    },
    useFormik: false,
  }

  return (
    <SelectInput
      key={tenantCollaborationId}
      {...restProps}
      {...options}
      value={value}
      handleChange={handleUserChange}
    />
  )
}

UserSelect.propTypes = {
  onChange: PropTypes.func,
  customSelectOptions: PropTypes.object,
  tenantCollaborationId: PropTypes.string,
  label: PropTypes.string,
  hasClientModuleAccess: PropTypes.bool,
  setResponsibleUserCount: PropTypes.func,
  hideTaxConsultantUsers: PropTypes.bool,
  withArrow: PropTypes.bool,
  name: PropTypes.string,
  exclude: PropTypes.array,
  excludeLocked: PropTypes.bool,
  addAllOption: PropTypes.bool,
  fileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

UserSelect.defaultProps = {
  customSelectOptions: {},
  tenantCollaborationId: 'internal',
  hasClientModuleAccess: false,
  hideTaxConsultantUsers: false,
  exclude: [],
  excludeLocked: false,
  addAllOption: false,
}
